import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Twitter, Github, Linkedin } from 'lucide-react';
import Button from '../ui/Button';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [botField, setBotField] = useState('');
  const [error, setError] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    if (botField) {
      return;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setError('');
    // TODO: Implement newsletter signup (server-side)
    console.log('Newsletter signup:', email);
    setSubscribed(true);
    setEmail('');
    setBotField('');
  };

  const currentYear = new Date().getFullYear();

  const footerLinks = {
    product: [
      { name: 'Home', path: '/' },
      { name: 'Pricing', path: '/pricing' },
      { name: 'Concrete Calculator', path: '/concrete-calculator' },
      { name: 'Drywall Calculator', path: '/drywall-calculator' },
      { name: 'Paint Calculator', path: '/paint-calculator' },
      { name: 'Flooring Calculator', path: '/flooring-calculator' },
    ],
    resources: [
      { name: 'About', path: '/about' },
      { name: 'Contact', path: '/contact' },
      { name: 'How It Works', path: '/how-it-works' },
      { name: 'Blog', path: '/blog' },
      { name: 'FAQ', path: '/faq' },
    ],
    legal: [
      { name: 'Privacy Policy', path: '/privacy' },
      { name: 'Terms of Service', path: '/terms' },
      { name: 'Cookie Policy', path: '/cookies' },
    ],
  };

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand column */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">CB</span>
              </div>
              <span className="font-bold text-xl text-white">
                Calc<span className="text-blue-400">Build</span>AI
              </span>
            </Link>
            <p className="text-gray-400 mb-6 max-w-sm">
              Calculate construction materials with AI. Save time, reduce waste, 
              and get accurate estimates for your projects.
            </p>
            
            {/* Newsletter */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-white mb-3">
                Get updates & tips
              </h4>
              {subscribed ? (
                <p className="text-green-400 text-sm">✓ Thanks for subscribing!</p>
              ) : (
                <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                  <div className="hidden" aria-hidden="true">
                    <label htmlFor="newsletter-company" className="sr-only">Company</label>
                    <input
                      id="newsletter-company"
                      name="company"
                      tabIndex="-1"
                      autoComplete="off"
                      value={botField}
                      onChange={(e) => setBotField(e.target.value)}
                    />
                  </div>
                  <input
                    id="newsletter-email"
                    name="newsletter-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    autoComplete="email"
                    className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <Button type="submit" variant="primary" size="md">
                    Subscribe
                  </Button>
                </form>
              )}
              {error && !subscribed && (
                <p className="text-sm text-red-400 mt-2">{error}</p>
              )}
            </div>

            {/* Social links */}
            <div className="flex gap-4">
              <button className="text-gray-400 hover:text-white transition-colors" aria-label="Twitter">
                <Twitter className="w-5 h-5" />
              </button>
              <button className="text-gray-400 hover:text-white transition-colors" aria-label="GitHub">
                <Github className="w-5 h-5" />
              </button>
              <button className="text-gray-400 hover:text-white transition-colors" aria-label="LinkedIn">
                <Linkedin className="w-5 h-5" />
              </button>
              <a href="mailto:support@calcbuildai.com" className="text-gray-400 hover:text-white transition-colors">
                <Mail className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Product links */}
          <div>
            <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">
              Product
            </h4>
            <ul className="space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.path + link.name}>
                  <Link
                    to={link.path}
                    className={`text-gray-400 hover:text-white transition-colors ${link.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={link.disabled ? (e) => e.preventDefault() : undefined}
                  >
                    {link.name}
                    {link.disabled && <span className="text-xs ml-2">(Soon)</span>}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources links */}
          <div>
            <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">
              Resources
            </h4>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.path + link.name}>
                  <Link
                    to={link.path}
                    className={`text-gray-400 hover:text-white transition-colors ${link.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={link.disabled ? (e) => e.preventDefault() : undefined}
                  >
                    {link.name}
                    {link.disabled && <span className="text-xs ml-2">(Soon)</span>}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal links */}
          <div>
            <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">
              Legal
            </h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">
            © {currentYear} CalcBuildAI. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <span>Made with ❤️ for builders</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
